/* eslint-disable indent */
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Button,
  Typography,
  Dialog,
  InputAdornment,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  CircularProgress,
  AlertColor
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMessages } from 'modules/intl';
import { postRequest } from 'utils/helper';
import { OauthPopupLoginAndRedirect } from 'modules/oauth/oauthPopupLoginAndRedirect';
import { MenuItemNames, InstallType, ConnectorInfo } from 'types/types';
import { ErrorPage } from 'modules/common/routes/ErrorPage';
import { ToastSnackBar } from '../Setting/ToastSnackBar';
import { DialogHeaderContext } from './DialogHeader';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2.5)
  },
  box: {
    display: 'flex',
    justifyContent: 'center'
  },
  image: {
    height: theme.spacing(12.5),
    width: theme.spacing(18.75)
  },
  textField: {
    marginBottom: theme.spacing(3.75)
  }
}));

type JiraInstallDialogProps = {
  info: ConnectorInfo;
  open: boolean;
  installName: string;
  handleInstallDialogClose: () => void;
};

const messages = ['intermediateInstallDialog.inputError'];

export const JiraInstallDialogPage: React.FC<JiraInstallDialogProps> = ({
  info,
  open,
  installName,
  handleInstallDialogClose
}: JiraInstallDialogProps) => {
  const [connected, setConnected] = useState(false);
  const [textField, setTextField] = useState({
    apiKey: '',
    authHostName: '',
    oauth2HostName: '',
    userName: ''
  });
  const [textfieldError, setTextfieldError] = useState({
    apiKeyError: '',
    authHostNameError: '',
    oauth2HostNameError: '',
    userNameError: ''
  });
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);

  const classes = useStyles();
  const errorMessage = useMessages(messages);
  const { formatMessage } = useIntl();

  const requiredInputError =
    errorMessage['intermediateInstallDialog.inputError'];

  const handleError = useCallback(
    (field: string, value: string) => {
      setTextfieldError((prev: any) => ({
        ...prev,
        [`${field}Error`]: value.trim() === '' ? requiredInputError : ''
      }));
    },
    [requiredInputError]
  );

  const handleChanges = useCallback(
    (field: string, event: any) => {
      const value = event.target.value;
      setTextField((prev: any) => ({
        ...prev,
        [field]: value
      }));
      handleError(field, value);
    },
    [handleError]
  );

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);
  const onSnackbarOpen = useCallback(() => {
    setIsSnackBarOpen(true);
  }, []);

  const handleBasicAuth = useCallback(async () => {
    setIsDisabled(true);
    setSeverity('success');
    onSnackbarOpen();
    try {
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        environment: 'production',
        isPsaProduct: info.isPsaProduct,
        hostName: `https://${textField.authHostName}.atlassian.net`,
        userName: textField.userName,
        apiKey: textField.apiKey,
        installMethod: InstallType.basicAuth
      };
      const res = await postRequest(
        '/integration-settings-api/install-basicauth-connector',
        body,
        false
      );
      if (res.data) {
        setConnected(true);
      }
    } catch (error: any) {
      if (error.response.status === 500) {
        setShowErrorPage(true);
      } else {
        setIsDisabled(false);
        setSeverity('error');
        onSnackbarOpen();
      }
    }
  }, [
    info.shortName,
    info.companyKey,
    info.isPsaProduct,
    textField.authHostName,
    textField.userName,
    textField.apiKey,
    onSnackbarOpen
  ]);

  const handleApiCallForOAuth2 = useCallback(async () => {
    try {
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        environment: 'production',
        isPsaProduct: info.isPsaProduct,
        hostName: textField.oauth2HostName,
        installMethod: InstallType.oauth2
      };
      const res = await postRequest(
        '/integration-settings-api/install-connector',
        body,
        false
      );
      if (res.data) {
        setConnected(true);
        OauthPopupLoginAndRedirect(res.data, info.name);
      }
    } catch (error: any) {
      onSnackbarOpen();
    }
  }, [
    info.shortName,
    info.companyKey,
    info.isPsaProduct,
    info.name,
    textField.oauth2HostName,
    onSnackbarOpen
  ]);

  const handleConnect = (event: any) => {
    event.preventDefault();
    const value = event.target.value;
    if (installName === MenuItemNames.OAuth2) {
      if (textField.oauth2HostName.trim() === '') {
        handleError('oauth2HostName', value);
      }
      if (textField.oauth2HostName.trim() !== '') {
        handleApiCallForOAuth2();
      }
    } else {
      let hasError = false;
      const fields: Array<keyof typeof textField> = [
        'apiKey',
        'authHostName',
        'userName'
      ];

      fields.forEach((field) => {
        if (textField[field].trim() === '') {
          handleError(field, value);
          hasError = true;
        }
      });
      if (!hasError) {
        setIsDisabled(true);
        handleBasicAuth();
      }
    }
  };

  const alertMessage = useMemo(() => {
    return severity === 'error'
      ? formatMessage({
          id: 'intermediateInstallDialog.snackBarErrorMessage'
        })
      : formatMessage({
          id: 'intermediateInstallDialog.waitingMessage'
        });
  }, [formatMessage, severity]);

  /* eslint-disable indent */
  const hostNameHelperText = useMemo(() => {
    if (installName === MenuItemNames.BasicAuth) {
      return textfieldError.authHostNameError
        ? textfieldError.authHostNameError
        : formatMessage({
            id: 'intermediateOauthDialog.hostName.jira.helperText'
          });
    } else {
      return textfieldError.oauth2HostNameError
        ? textfieldError.oauth2HostNameError
        : formatMessage({
            id: 'intermediateOauthDialog.hostName.jira.helperText'
          });
    }
  }, [
    textfieldError.authHostNameError,
    textfieldError.oauth2HostNameError,
    formatMessage,
    installName
  ]);

  setTimeout(() => {
    if (connected) {
      if (installName === MenuItemNames.BasicAuth) {
        window.location.reload();
      } else {
        handleInstallDialogClose();
        setConnected(false);
      }
    }
  }, 2000);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleInstallDialogClose}
        data-testid="check-all-contents"
      >
        <DialogTitle
          className={classes.header}
          data-testid="check-dialog-title"
        >
          <FormattedMessage id={'intermediateInstallDialog.header'} />
        </DialogTitle>
        <DialogContent>
          <DialogHeaderContext shortName={info.shortName} />
          <Box className={classes.box} data-testid="render-box">
            <div>
              <TextField
                id="hostName"
                variant="outlined"
                required
                fullWidth
                value={
                  installName === MenuItemNames.BasicAuth
                    ? textField.authHostName
                    : textField.oauth2HostName
                }
                onChange={(e: any) =>
                  handleChanges(
                    installName === MenuItemNames.BasicAuth
                      ? 'authHostName'
                      : 'oauth2HostName',
                    e
                  )
                }
                error={
                  installName === MenuItemNames.BasicAuth
                    ? Boolean(textfieldError.authHostNameError)
                    : Boolean(textfieldError.oauth2HostNameError)
                }
                label={
                  <FormattedMessage
                    id={'intermediateOauthDialog.hostName.label'}
                  />
                }
                type={'text'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="body1"
                        data-testid="check-hostName-startAdornment"
                      >
                        <FormattedMessage
                          id={'intermediateOauthDialog.hostName.startAdornment'}
                        />
                      </Typography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        variant="body1"
                        data-testid="check-hostName-endAdornment"
                      >
                        <FormattedMessage
                          id={
                            'intermediateOauthDialog.hostName.jira.endAdornment'
                          }
                        />
                      </Typography>
                    </InputAdornment>
                  )
                }}
                helperText={hostNameHelperText}
                className={classes.textField}
                inputProps={{ 'data-testid': 'hostNameInput' }}
              />
              <br />
              {installName === MenuItemNames.BasicAuth ? (
                <>
                  <TextField
                    id="userName"
                    variant="outlined"
                    required
                    fullWidth
                    value={textField.userName}
                    onChange={(event) => {
                      handleChanges('userName', event);
                    }}
                    error={Boolean(textfieldError.userNameError)}
                    label={
                      <FormattedMessage
                        id={'intermediateOauthDialog.jiraAuth.userNameLabel'}
                      />
                    }
                    InputLabelProps={{
                      shrink: true
                    }}
                    type={'text'}
                    helperText={
                      textfieldError.userNameError ? (
                        textfieldError.userNameError
                      ) : (
                        <FormattedMessage
                          id={
                            'intermediateOauthDialog.jiraAuth.userNameHelperText'
                          }
                        />
                      )
                    }
                    className={classes.textField}
                    inputProps={{ 'data-testid': 'userNameInput' }}
                  />
                  <br />
                  <TextField
                    id={'apiKey'}
                    variant="outlined"
                    required
                    fullWidth
                    value={textField.apiKey}
                    onChange={(event) => {
                      handleChanges('apiKey', event);
                    }}
                    error={Boolean(textfieldError.apiKeyError)}
                    label={
                      <FormattedMessage
                        id={'intermediateOauthDialog.jiraAuth.apiKeyLabel'}
                      />
                    }
                    type={'password'}
                    helperText={
                      textfieldError.apiKeyError ? (
                        textfieldError.apiKeyError
                      ) : (
                        <FormattedMessage
                          id={
                            'intermediateOauthDialog.jiraAuth.apiKeyHelperText'
                          }
                        />
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          data-testid="check-apiKey-startAdornment"
                        >
                          <LockIcon />
                        </InputAdornment>
                      )
                    }}
                    className={classes.textField}
                    inputProps={{ 'data-testid': 'api-key-Input' }}
                  />
                </>
              ) : null}
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleInstallDialogClose}
            color="grey"
            autoFocus
            variant="contained"
            disabled={isDisabled}
            data-testid="cancel-button"
          >
            <FormattedMessage id={'connectorSetting.cancel'} />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleConnect}
            disabled={isDisabled}
            data-testid="connect-button"
          >
            {isDisabled ? (
              <Box display="flex" alignItems="center">
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                <FormattedMessage id="intermediateInstallDialog.installing" />
              </Box>
            ) : (
              <FormattedMessage
                id={'intermediateInstallDialog.connectButton'}
              />
            )}
          </Button>
        </DialogActions>
        <ToastSnackBar
          show={isSnackBarOpen}
          category={severity}
          message={alertMessage}
          onClose={onSnackbarClose}
        />
      </Dialog>
    </>
  );
};
